import { useMemo, useState } from 'react';
import { ActiveProcess } from '../types';
import { Loader2, Eye, Zap, StopCircle } from 'lucide-react';
import BodyModal from './BodyModal';
import { isDashboardExcludedUrl } from '../utils/dashboardFilters';

interface ActiveProcessesProps {
  processes: ActiveProcess[];
  onAbort?: (requestLogId: string) => Promise<void>;
}

export default function ActiveProcesses({ processes, onAbort }: ActiveProcessesProps) {
  const [selectedBody, setSelectedBody] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [abortingId, setAbortingId] = useState<string | null>(null);

  const handleViewBody = (body?: string) => {
    setSelectedBody(body);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBody(undefined);
  };

  const handleAbort = async (requestLogId: string) => {
    if (!onAbort || abortingId) return;
    if (!window.confirm('Deseja abortar este processo?')) return;
    setAbortingId(requestLogId);
    try {
      await onAbort(requestLogId);
    } finally {
      setAbortingId(null);
    }
  };

  const visibleProcesses = useMemo(
    () => (processes ?? []).filter((p) => !isDashboardExcludedUrl(p.url)),
    [processes]
  );
  const hasActive = visibleProcesses.length > 0;

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border-2 border-amber-200 p-6 ring-1 ring-amber-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            {hasActive ? (
              <Loader2 className="h-5 w-5 text-amber-600 animate-spin" aria-hidden />
            ) : (
              <Zap className="h-5 w-5 text-amber-600" aria-hidden />
            )}
            <h3 className="text-lg font-semibold text-gray-900">
              Processos em execução
              <span className="ml-2 text-base font-normal text-gray-500">
                ({visibleProcesses.length})
              </span>
            </h3>
          </div>
          <p className="text-xs text-gray-500">Atualização automática a cada 10 segundos</p>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Requisições HTTP que estão sendo processadas neste momento pela API.
        </p>
        {visibleProcesses.length === 0 ? (
          <div className="py-8 text-center rounded-lg bg-gray-50 border border-dashed border-gray-200">
            <p className="text-sm text-gray-500">Nenhum processo em execução no momento.</p>
            <p className="text-xs text-gray-400 mt-1">Novas requisições aparecerão aqui em tempo real.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Método
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    URL
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Início
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    IP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Body
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleProcesses.map((proc) => (
                  <tr key={proc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {proc.method}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={proc.url}>
                      {proc.url}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(proc.timestamp).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {proc.ip}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {proc.body ? (
                        <button
                          onClick={() => handleViewBody(proc.body)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="Ver body da requisição"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Ver</span>
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {onAbort ? (
                        <button
                          onClick={() => handleAbort(proc.id)}
                          disabled={abortingId === proc.id}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Abortar processo"
                        >
                          {abortingId === proc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <StopCircle className="h-4 w-4" />
                          )}
                          <span>{abortingId === proc.id ? 'Abortando...' : 'Abortar'}</span>
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <BodyModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        body={selectedBody}
        title="Request Body - Em execução"
      />
    </>
  );
}
